import axios from 'axios';

// In-memory cache for translations
// Structure: { [targetLanguage]: { [text]: translatedText } }
const translationCache = {};

// Store original text content for restoration when switching back to English
// Structure: { [nodeId]: originalText }
const originalTextCache = {};
let nodeIdCounter = 0;

/**
 * Translates text using Google Translate API
 * @param {string} text - The text to translate
 * @param {string} targetLanguage - The target language code (e.g., 'es', 'fr')
 * @returns {Promise<string>} - The translated text
 */
export const translateText = async (text, targetLanguage) => {
  // Skip translation for empty strings or if target language is English
  if (!text || targetLanguage === 'en') return text;
  
  // Check cache first
  if (translationCache[targetLanguage]?.[text]) {
    return translationCache[targetLanguage][text];
  }
  
  try {
    // Call Google Translate API
    const response = await axios.post(
      `https://translation.googleapis.com/language/translate/v2?key=${process.env.REACT_APP_GOOGLE_TRANSLATE_API_KEY}`,
      {
        q: text,
        target: targetLanguage,
        format: 'html' // Use 'html' to preserve HTML tags
      }
    );
    
    const translatedText = response.data.data.translations[0].translatedText;
    
    // Initialize language cache if it doesn't exist
    if (!translationCache[targetLanguage]) {
      translationCache[targetLanguage] = {};
    }
    
    // Cache the result
    translationCache[targetLanguage][text] = translatedText;
    
    return translatedText;
  } catch (error) {
    console.error('Translation error:', error);
    return text; // Return original text on error
  }
};

/**
 * Batch translates an array of texts
 * @param {string[]} texts - Array of texts to translate
 * @param {string} targetLanguage - The target language code
 * @returns {Promise<string[]>} - Array of translated texts
 */
export const batchTranslate = async (texts, targetLanguage) => {
  // Skip translation for empty arrays or if target language is English
  if (!texts.length || targetLanguage === 'en') return texts;
  
  // Check which texts are already in cache
  const cachedTexts = {};
  const textsToTranslate = [];
  const resultIndexMap = {};
  
  texts.forEach((text, index) => {
    if (translationCache[targetLanguage]?.[text]) {
      cachedTexts[index] = translationCache[targetLanguage][text];
    } else {
      resultIndexMap[textsToTranslate.length] = index;
      textsToTranslate.push(text);
    }
  });
  
  // If all texts are cached, return them
  if (textsToTranslate.length === 0) {
    return texts.map((_, index) => cachedTexts[index]);
  }
  
  try {
    const response = await axios.post(
      `https://translation.googleapis.com/language/translate/v2?key=${process.env.REACT_APP_GOOGLE_TRANSLATE_API_KEY}`,
      {
        q: textsToTranslate,
        target: targetLanguage,
        format: 'html'
      }
    );
    
    const translations = response.data.data.translations.map(t => t.translatedText);
    
    // Initialize language cache if it doesn't exist
    if (!translationCache[targetLanguage]) {
      translationCache[targetLanguage] = {};
    }
    
    // Cache the results
    translations.forEach((translation, index) => {
      const originalIndex = resultIndexMap[index];
      const originalText = texts[originalIndex];
      translationCache[targetLanguage][originalText] = translation;
      cachedTexts[originalIndex] = translation;
    });
    
    // Return the results in the original order
    return texts.map((_, index) => cachedTexts[index] || texts[index]);
  } catch (error) {
    console.error('Batch translation error:', error);
    return texts; // Return original texts on error
  }
};

/**
 * Translates DOM text nodes
 * This can be used to translate the entire page
 * @param {string} targetLanguage - The target language code
 */
export const translatePage = async (targetLanguage) => {
  // Skip if target language is English
  if (targetLanguage === 'en') return;
  
  // Get all text nodes in the document
  const textNodes = [];
  const walk = document.createTreeWalker(
    document.body,
    NodeFilter.SHOW_TEXT,
    {
      acceptNode: function(node) {
        // Skip empty text nodes and script/style content
        if (node.nodeValue.trim() === '' || 
            ['SCRIPT', 'STYLE', 'NOSCRIPT'].includes(node.parentNode.nodeName)) {
          return NodeFilter.FILTER_REJECT;
        }
        
        // Skip nodes that are children of elements with the 'no-translate' class
        let parent = node.parentNode;
        while (parent) {
          if (parent.classList && parent.classList.contains('no-translate')) {
            return NodeFilter.FILTER_REJECT;
          }
          parent = parent.parentNode;
        }
        
        return NodeFilter.FILTER_ACCEPT;
      }
    }
  );
  
  while (walk.nextNode()) {
    textNodes.push(walk.currentNode);
  }
  
  // Store original text before translating (only if not already stored)
  textNodes.forEach(node => {
    if (!node._translationNodeId) {
      const nodeId = `node_${nodeIdCounter++}`;
      node._translationNodeId = nodeId;
      originalTextCache[nodeId] = node.nodeValue;
    }
  });
  
  // Group text nodes to reduce API calls (Google Translate has limits)
  const batchSize = 100;
  const batches = [];
  
  for (let i = 0; i < textNodes.length; i += batchSize) {
    batches.push(textNodes.slice(i, i + batchSize));
  }
  
  // Translate each batch
  for (const batch of batches) {
    const textsToTranslate = batch.map(node => node.nodeValue);
    
    try {
      const translations = await batchTranslate(textsToTranslate, targetLanguage);
      
      // Update the text nodes with translations
      translations.forEach((translation, index) => {
        batch[index].nodeValue = translation;
      });
    } catch (error) {
      console.error('Translation error:', error);
    }
  }
};

/**
 * Restores original English text for all translated DOM nodes
 */
export const restoreOriginalText = () => {
  // Get all text nodes that have been translated (have _translationNodeId)
  const walk = document.createTreeWalker(
    document.body,
    NodeFilter.SHOW_TEXT,
    {
      acceptNode: function(node) {
        // Only accept nodes that have been translated (have _translationNodeId)
        return node._translationNodeId ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
      }
    }
  );
  
  const translatedNodes = [];
  while (walk.nextNode()) {
    translatedNodes.push(walk.currentNode);
  }
  
  // Restore original text
  translatedNodes.forEach(node => {
    const nodeId = node._translationNodeId;
    if (nodeId && originalTextCache[nodeId]) {
      node.nodeValue = originalTextCache[nodeId];
    }
  });
  
  console.log(`Restored ${translatedNodes.length} text nodes to original English`);
};
